import { pickT } from "@repo/shared";
import { TIMESERIES_STALE_DAYS, type TimeseriesInstrumentStatus } from "@repo/shared/types";
import { T } from "./status-summary.i18n";

export function daysSince(date: string | null): number | null {
  if (!date) return null;
  const ms = Date.now() - new Date(`${date}T00:00:00Z`).getTime();
  return Math.floor(ms / 86_400_000);
}

/**
 * Roll the row's conditions up to one traffic-light level for the admin:
 * red = broken (no data / sync disabled), orange = needs attention
 * (stale, missing currency, failing sync), green = all good.
 * Shared by the table's status dot and the inspect dialog's callout.
 */
export function statusSummary(
  item: TimeseriesInstrumentStatus,
  lang: string,
): { level: "ok" | "warn" | "error"; reasons: string[] } {
  const t = pickT(T, lang);
  const errors: string[] = [];
  const warns: string[] = [];
  if (item.pointCount === 0) errors.push(t.noData);
  if (item.syncDisabledAt) {
    errors.push(
      `${t.syncDisabled} (${item.syncFailures})${item.syncError ? `: ${item.syncError}` : ""}`,
    );
  } else if (item.syncFailures > 0) {
    warns.push(
      `${t.syncFailing} (${item.syncFailures})${item.syncError ? `: ${item.syncError}` : ""}`,
    );
  }
  if (!item.currency) warns.push(t.noCurrency);
  if (item.fxMissing) warns.push(`${t.fxMissing} (${item.currency})`);
  const age = daysSince(item.lastDate);
  if (item.pointCount > 0 && age !== null && age > TIMESERIES_STALE_DAYS) {
    warns.push(`${t.stale} (${age}d)`);
  }
  if (errors.length > 0) return { level: "error", reasons: [...errors, ...warns] };
  if (warns.length > 0) return { level: "warn", reasons: warns };
  return { level: "ok", reasons: [t.ok] };
}
